import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Skeleton,
  useMediaQuery,
  useTheme,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import GridViewIcon from "@mui/icons-material/GridView";
import allServices from "../../assets/allServices.svg";
import {
  useSearchPopularServiceCategories,
  ServiceCategoryItem,
  ServiceCategoryApiResponse,
} from "../../api-data/service-provider/service-categories";
import { useNavigate } from "react-router-dom";
import { useTrackEvent } from "../../hooks/useTrackEvent";
import { EventType } from "../../api-data/reporting/enums";

interface PopularSearchesProps {
  onSelect: (keyword: string, categoryId: number) => void;
  currentCity?: string;
  currentLocality?: string;
  currentCityId?: number;
}

const PopularSearches: React.FC<PopularSearchesProps> = ({
  onSelect,
  currentCity,
  currentLocality,
  currentCityId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackEvent } = useTrackEvent();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("md"));
  const { data: categoriesResponse, isFetching } =
    useSearchPopularServiceCategories(50, 0, undefined, currentCity);

  const popularCategories = useMemo(() => {
    const categories =
      categoriesResponse?.pages?.flatMap(
        (p: ServiceCategoryApiResponse) => p.data,
      ) || [];

    return categories.filter(
      (cat: ServiceCategoryItem) => cat.isPopularSearch === true,
    );
  }, [categoriesResponse]);

  const handleMoreClick = () => {
    window.scrollTo(0, 0);
    navigate(`/all-service-categories`);
  };

  // OOTWUI-018: no popular category has a provider in this city, so hide the whole section.
  if (!isFetching && popularCategories.length === 0) return null;

  // Determine how many items to show and skeleton count
  const displayItems = isXs ? popularCategories.slice(0, 8) : popularCategories.slice(0, 11);
  const skeletonCount = isXs ? 8 : 11;

  // Mobile — native-app 3-column tile grid (square icon tiles, capped to 8
  // real categories + a "More" tile in the same grid, matching the mockup).
  // Desktop below is untouched — separate layout, separate mockup.
  if (isXs) {
    return (
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1.75 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "primary.dark" }}>
            {t("home.popularServicesMobile")}
          </Typography>
          <Typography
            onClick={handleMoreClick}
            sx={{ fontSize: 13, fontWeight: 600, color: "primary.main", cursor: "pointer" }}
          >
            {t("home.seeAll")}
          </Typography>
        </Box>

        <Grid container spacing={1} columns={3}>
          {isFetching &&
            Array.from({ length: skeletonCount }).map((_, index) => (
              <Grid key={`skeleton-${index}`} size={1}>
                <Skeleton variant="rounded" sx={{ width: "100%", aspectRatio: "1 / 1", borderRadius: "16px" }} />
              </Grid>
            ))}

          {!isFetching &&
            displayItems.map((item: ServiceCategoryItem) => {
              const displayName = item.localized?.categoryName ?? item.categoryName;
              return (
                <Grid key={item.id} size={1}>
                  <Box
                    onClick={() => {
                      if (currentCity) {
                        trackEvent(EventType.CATEGORY_SELECTED, {
                          categoryId: item.id,
                          cityId: currentCityId ?? null,
                          source: "popular_searches",
                        });
                        onSelect(item.categoryName, item.id);
                      }
                    }}
                    sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, cursor: "pointer" }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        borderRadius: "16px",
                        bgcolor: "#fff",
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: "0 1px 3px rgba(0,0,0,.06)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 1,
                        boxSizing: "border-box",
                      }}
                    >
                      <Box
                        component="img"
                        src={item.imageUrl || allServices}
                        alt={displayName}
                        sx={{ width: "96%", height: "96%", objectFit: "contain", display: "block" }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: 1.25,
                        color: "text.primary",
                        textAlign: "center",
                      }}
                    >
                      {displayName}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}

          {!isFetching && popularCategories.length > 0 && (
            <Grid size={1}>
              <Box
                onClick={handleMoreClick}
                sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, cursor: "pointer" }}
              >
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "1 / 1",
                    borderRadius: "16px",
                    bgcolor: "primary.focus",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <GridViewIcon sx={{ fontSize: 30, color: "primary.main" }} />
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: "text.primary" }}>
                  {t("home.moreTile")}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  }

  // Desktop — 6-column icon tile grid (DesktopHome.jsx), capped to 11 real
  // categories + a "More" tile in the same grid, heading + "See all" link.
  return (
    <Box sx={{ mb: 7 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 3.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 24, color: "primary.dark" }}>
          {t("home.popularSearches")}
        </Typography>
        <Typography
          onClick={handleMoreClick}
          sx={{ fontSize: "14.5px", fontWeight: 600, color: "primary.main", cursor: "pointer" }}
        >
          {t("home.seeAllArrow")}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {isFetching &&
          Array.from({ length: skeletonCount }).map((_, index) => (
            <Grid key={`skeleton-${index}`} size={2}>
              <Skeleton variant="rounded" sx={{ width: "100%", aspectRatio: "1 / 1", borderRadius: "20px" }} />
            </Grid>
          ))}

        {!isFetching &&
          displayItems.map((item: ServiceCategoryItem) => {
            const displayName = item.localized?.categoryName ?? item.categoryName;
            return (
              <Grid key={item.id} size={2}>
                <Box
                  onClick={() => {
                    if (currentCity) {
                      trackEvent(EventType.CATEGORY_SELECTED, {
                        categoryId: item.id,
                        cityId: currentCityId ?? null,
                        source: "popular_searches",
                      });
                      onSelect(item.categoryName, item.id);
                    }
                  }}
                  sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, cursor: "pointer" }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      aspectRatio: "1 / 1",
                      borderRadius: "20px",
                      bgcolor: "#fff",
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: "0 1px 3px rgba(0,0,0,.06)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1.75,
                      boxSizing: "border-box",
                      transition: "transform .18s, box-shadow .18s",
                      "&:hover": { transform: "translateY(-4px)", boxShadow: "0 8px 24px rgba(0,0,0,.1)" },
                    }}
                  >
                    <Box
                      component="img"
                      src={item.imageUrl || allServices}
                      alt={displayName}
                      sx={{ width: "94%", height: "94%", objectFit: "contain", display: "block" }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.25, color: "text.primary", textAlign: "center" }}>
                    {displayName}
                  </Typography>
                </Box>
              </Grid>
            );
          })}

        {!isFetching && popularCategories.length > 0 && (
          <Grid size={2}>
            <Box
              onClick={handleMoreClick}
              sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, cursor: "pointer" }}
            >
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "1 / 1",
                  borderRadius: "20px",
                  bgcolor: "primary.focus",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "transform .18s, box-shadow .18s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: "0 8px 24px rgba(0,0,0,.1)" },
                }}
              >
                <GridViewIcon sx={{ fontSize: 40, color: "primary.main" }} />
              </Box>
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                {t("home.moreTile")}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PopularSearches;
